// Evaluation Analytics Provider
//
// This provider handles evaluation-specific event tracking by sending evaluation data
// to webhook endpoints (like N8N). It migrates logic from the evaluation service
// to work with the new provider architecture and focuses only on evaluation events.

import type { NetworkAdapter } from '../../adapters/network';
import { logger } from '../../utils/core/logger';
import { getFullDomain } from '../../utils/domain/domain';
import type { AnalyticsEvent } from '../types';
import { EventCategories, EventTypes } from '../types';
import { SimpleAnalyticsProvider } from './base';

export interface EvaluationConfig {
    evaluationUrl: string;
    siteName: string;
    networkAdapter?: NetworkAdapter;
}

interface EvaluationData {
    rating:
        | 'Accurate & OK'
        | 'Accurate but Not OK'
        | 'Not Accurate but OK'
        | 'Not Accurate & Not OK'
        | 'thumbs_up'
        | 'thumbs_down';
    comment?: string;
    messageText: string;
    messageId: string;
    url: string;
    domain: string;
    pageTitle: string;
    timestamp: string;
    sessionId?: string;
    threadId?: string;
}

export class EvaluationProvider extends SimpleAnalyticsProvider {
    public readonly name = 'evaluation';

    private config: EvaluationConfig;

    constructor(config: EvaluationConfig, timeoutMs: number = 5000) {
        super(timeoutMs);
        this.config = config;
    }

    public eventFilter(event: AnalyticsEvent): boolean {
        // Only handle evaluation-related events
        if (event.category === EventCategories.EVALUATION) {
            return true;
        }

        // Also handle specific evaluation event types
        const evaluationEvents = [
            EventTypes.EVALUATION_SUBMITTED,
            EventTypes.EVALUATION_POPUP_SHOWN,
            EventTypes.EVALUATION_POPUP_CANCELLED,
            EventTypes.EVALUATION_THUMBS_CLICKED,
        ];

        return evaluationEvents.includes(event.type as (typeof evaluationEvents)[number]);
    }

    protected async sendEventImpl(event: AnalyticsEvent): Promise<void> {
        // Only send actual evaluation submissions to the webhook
        // Other evaluation events (popup shown, cancelled, etc.) are for analytics only
        if (event.type !== EventTypes.EVALUATION_SUBMITTED) {
            logger.debug(`Evaluation provider: Ignoring non-submission event: ${event.type}`);
            return;
        }

        // Convert analytics event to evaluation data format
        const evaluationData = this.convertToEvaluationData(event);

        logger.debug(
            'EvaluationProvider: Submitting evaluation for messageId:',
            evaluationData.messageId
        );

        try {
            // Try to submit via the configured network method
            const result = await this.callWebhookViaNetwork(evaluationData);

            if (
                result &&
                typeof result === 'object' &&
                'success' in result &&
                result.success === false
            ) {
                this.saveEvaluationLocally(evaluationData);
                logger.debug(
                    'EvaluationProvider: Remote endpoint reported failure, saved locally as fallback'
                );
                return;
            }

            logger.debug('EvaluationProvider: Evaluation submitted successfully via network');
            return;
        } catch (error) {
            logger.error('EvaluationProvider: Error submitting evaluation:', error);

            // Save locally as fallback
            this.saveEvaluationLocally(evaluationData);
            throw error; // Re-throw to let event bus handle the error
        }
    }

    private convertToEvaluationData(event: AnalyticsEvent): EvaluationData {
        const props = event.properties;
        const context = event.context;

        if (!context.domain) {
            throw new Error('Domain not available in analytics context');
        }

        return {
            rating: (props.rating as EvaluationData['rating']) || 'thumbs_up',
            comment: props.comment as string | undefined,
            messageText: (props.messageText as string) || '',
            messageId: (props.messageId as string) || `msg_${Date.now()}`,
            url:
                (props.url as string) ||
                (typeof window !== 'undefined' ? window.location.href : ''),
            domain: context.domain,
            pageTitle:
                (props.pageTitle as string) ||
                (typeof document !== 'undefined' ? document.title : ''),
            timestamp: new Date(event.timestamp).toISOString(),
            sessionId: context.sessionId,
            threadId: props.threadId as string | undefined,
        };
    }

    private async callWebhookViaNetwork(data: EvaluationData): Promise<unknown> {
        // Get proxy endpoint from environment
        const proxyEndpoint = import.meta.env.VITE_EVALUATION_ENDPOINT;

        if (!proxyEndpoint) {
            // Fallback to direct webhook call if proxy not configured
            logger.warn(
                'EvaluationProvider: VITE_EVALUATION_ENDPOINT not configured, using direct webhook'
            );
            return this.directWebhookCall(data);
        }

        // Prepare wrapped payload for proxy
        const proxyPayload = {
            webhook_url: this.config.evaluationUrl,
            data: data,
        };

        // Use network adapter if available
        if (this.config.networkAdapter) {
            try {
                const response = await this.config.networkAdapter.post(proxyEndpoint, proxyPayload);
                return response;
            } catch (error) {
                logger.error('EvaluationProvider: Network adapter failed', error, {
                    provider: 'evaluation',
                    endpoint: proxyEndpoint,
                    webhookUrl: this.config.evaluationUrl,
                    evaluationData: data,
                });
                throw error;
            }
        }

        // Fallback to direct fetch through proxy
        try {
            const response = await fetch(proxyEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(proxyPayload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Handle 204 No Content and other responses without body
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return { success: true };
            }

            try {
                const text = await response.text();
                return text ? JSON.parse(text) : { success: true };
            } catch {
                // If response has no JSON body, treat as success (webhook accepted the data)
                logger.debug('EvaluationProvider: Response has no JSON body, treating as success');
                return { success: true };
            }
        } catch (error) {
            // logger.error automatically sends to Sentry in production with full context
            logger.error('EvaluationProvider: Proxy request failed', error, {
                provider: 'evaluation',
                endpoint: proxyEndpoint,
                webhookUrl: this.config.evaluationUrl,
                evaluationData: data,
            });
            throw error;
        }
    }

    /**
     * Direct webhook call (fallback when proxy not configured)
     */
    private async directWebhookCall(data: EvaluationData): Promise<unknown> {
        try {
            const response = await fetch(this.config.evaluationUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Handle 204 No Content and other responses without body
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return { success: true };
            }

            try {
                const text = await response.text();
                return text ? JSON.parse(text) : { success: true };
            } catch {
                // If response has no JSON body, treat as success (webhook accepted the data)
                logger.debug('EvaluationProvider: Response has no JSON body, treating as success');
                return { success: true };
            }
        } catch (error) {
            logger.error('EvaluationProvider: Direct fetch failed', error, {
                provider: 'evaluation',
                endpoint: this.config.evaluationUrl,
                evaluationData: data,
            });
            throw error;
        }
    }

    private saveEvaluationLocally(evaluationData: EvaluationData): void {
        try {
            const evaluations = JSON.parse(localStorage.getItem('llm_evaluations') || '[]');
            evaluations.push(evaluationData);
            localStorage.setItem('llm_evaluations', JSON.stringify(evaluations));
        } catch (error) {
            logger.error('EvaluationProvider: Error saving evaluation locally:', error);
        }
    }

    public updateConfig(newConfig: Partial<EvaluationConfig>): void {
        this.config = { ...this.config, ...newConfig };
        logger.debug('EvaluationProvider: Config updated:', this.config);
    }

    public getConfig(): EvaluationConfig {
        return { ...this.config };
    }

    /**
     * Helper method to submit an evaluation (maintains compatibility with original API)
     */
    public async submitEvaluation(evaluationData: Partial<EvaluationData>): Promise<boolean> {
        try {
            // Convert evaluation data to analytics event format
            const event: AnalyticsEvent = {
                type: EventTypes.EVALUATION_SUBMITTED,
                properties: evaluationData,
                context: this.getContext(),
                timestamp: Date.now(),
                category: EventCategories.EVALUATION,
            };

            await this.track(event);
            return true;
        } catch (error) {
            logger.error('EvaluationProvider: Error in submitEvaluation:', error);
            return false;
        }
    }

    /**
     * Save evaluation state to session storage
     */
    public saveEvaluationState(messageId: string): void {
        try {
            const domain = getFullDomain();
            const evaluationsKey = `evaluations_${domain}`;

            const existingEvaluations =
                typeof sessionStorage !== 'undefined'
                    ? sessionStorage.getItem(evaluationsKey)
                    : null;
            const evaluations = existingEvaluations ? JSON.parse(existingEvaluations) : [];

            if (!evaluations.includes(messageId)) {
                evaluations.push(messageId);
                if (typeof sessionStorage !== 'undefined') {
                    sessionStorage.setItem(evaluationsKey, JSON.stringify(evaluations));
                }
            }
        } catch (error) {
            logger.error('EvaluationProvider: Error saving evaluation state:', error);
        }
    }

    /**
     * Check if a message has been evaluated
     */
    public isMessageEvaluated(messageId: string): boolean {
        try {
            const domain = getFullDomain();
            const evaluationsKey = `evaluations_${domain}`;
            const saved =
                typeof sessionStorage !== 'undefined'
                    ? sessionStorage.getItem(evaluationsKey)
                    : null;
            const evaluations = saved ? JSON.parse(saved) : [];
            return evaluations.includes(messageId);
        } catch (error) {
            logger.error('EvaluationProvider: Error checking evaluation state:', error);
            return false;
        }
    }
}
